// Master KL registry.
// Zobrazuje schválené master_certificates (jeden KL per přístroj×intake).
// Per-probe legacy certs jsou k dispozici přes
// `/api/certificates` (legacy endpoint), ale nejsou zde primárně listed.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Search, FileCode, FileDown, FileSpreadsheet, Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import RefLink from '@/components/RefLink';
import SortableTh from '@/components/SortableTh';
import useSortable from '@/lib/useSortable';
import exportCsv from '@/lib/exportCsv';
import { Checkbox } from '@/components/ui/checkbox';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';

export default function CertificateRegistryPage() {
  const { t } = useTranslation();
  const [certificates, setCerts] = useState([]);
  const [search, setSearch] = useState('');

  const load = () => {
    api.get('/master-certificates?status=approved').then(r => setCerts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = certificates.filter(c => {
    const q = search.toLowerCase();
    const hay = [c.certificateNumber, c.instrumentName, c.instrumentType,
      c.serialNumber, c.customerName, c.orderNumber, c.intakeProtocolNumber,
      ...(c.laboratories || [])].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  });

  const { sorted, sortKey, sortDir, toggleSort } = useSortable(filtered, 'approvedAt', 'desc');

  const sel = useBulkSelect(sorted.map(c => c.id));

  const removeCert = async (certId, certNumber) => {
    if (!window.confirm(`Smazat kalibrační list ${certNumber || ''}? Tuto akci nelze vrátit.`)) return;
    try {
      await api.delete(`/master-certificates/${certId}`);
      load();
      toast.success('Kalibrační list smazán');
    } catch (err) {
      toastApiError(err);
    }
  };

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/master-certificates/${id}`));
    sel.clear();
    load();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} selhalo`);
    else toast.success(`Smazáno ${ok}`);
  };

  const downloadDccXml = (certId, certNumber) => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/master-certificates/${certId}/dcc-xml`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${certNumber || 'DCC'}.xml`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error(t('certificates.error-dcc-unavailable')));
  };

  const downloadCertificatePdf = (certId, certNumber) => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/master-certificates/${certId}/certificate.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.detail || t('certificates.error-pdf-cannot-download')); });
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${certNumber || 'KL'}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => toastApiError(err, t('certificates.error-pdf-unavailable')));
  };

  // Tisk štítku
  const downloadLabel = (certId, certNumber) => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/master-certificates/${certId}/label.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.detail || t('certificates.error-label-cannot-download')); });
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${t('certificates.label-filename-prefix')}-${certNumber || 'KL'}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
        load();  // refresh — backend označil labelPrinted=true
      })
      .catch(err => toastApiError(err, t('certificates.error-label-failed')));
  };

  return (
    <div data-testid="certificate-registry-page" className="space-y-4 h-[calc(100vh-7rem)] flex flex-col">
      <div>
        <h1 className="ct-h1">{t('certificates.title')}</h1>
        <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
          {t('certificates.subtitle')}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ct-text-secondary)' }} />
          <Input
            data-testid="certificate-search"
            placeholder={t('certificates.search-placeholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-9 text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => exportCsv({
            filename: `${t('certificates.csv-filename-prefix')}-${new Date().toISOString().slice(0, 10)}.csv`,
            rows: sorted,
            columns: [
              { key: 'certificateNumber', label: t('certificates.col-cert-number') },
              { key: 'approvedAt', label: t('certificates.col-issued-at'), format: v => v ? new Date(v).toLocaleDateString(t('common.date-locale')) : '' },
              { key: 'instrumentName', label: t('certificates.col-instrument') },
              { key: 'instrumentType', label: t('certificates.col-instrument-type') },
              { key: 'serialNumber', label: t('certificates.col-serial-number') },
              { key: 'laboratories', label: t('certificates.col-laboratories'), format: v => (v || []).join(', ') },
              { key: 'calibrationCount', label: t('certificates.col-probes-count') },
              { key: 'orderNumber', label: t('certificates.col-order') },
              { key: 'customerName', label: t('certificates.col-customer') },
              { key: 'approvedByName', label: t('certificates.col-approved-by') },
            ],
          })}
          disabled={sorted.length === 0}
          className="h-9 flex items-center gap-1.5 px-3 text-sm rounded border transition-colors disabled:opacity-40"
          style={{ background: 'var(--ct-surface)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
          title={t('certificates.csv-export-title', { count: sorted.length })}
        >
          <FileSpreadsheet className="w-4 h-4" />
          {t('certificates.csv-export-btn')}
        </button>
      </div>

      <div className="ct-panel flex-1 flex flex-col">
        <div className="ct-panel-header flex items-center gap-2">
          <FileText className="w-4 h-4" />
          {t('certificates.panel-title', { count: filtered.length })}
        </div>
        {sel.count > 0 && (
          <div className="px-3 pt-2">
            <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="kalibračních listů" />
          </div>
        )}
        <ScrollArea className="flex-1">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--ct-border)' }}>
                <th className="px-2 py-2 w-8">
                  <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} aria-label="Vybrat vše" />
                </th>
                <SortableTh column="certificateNumber" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('certificates.col-cert-number')}</SortableTh>
                <SortableTh column="approvedAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('certificates.col-issued-at')}</SortableTh>
                <SortableTh column="instrumentName" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('certificates.col-instrument')}</SortableTh>
                <SortableTh column="instrumentType" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('certificates.col-instrument-type')}</SortableTh>
                <SortableTh column="serialNumber" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('certificates.col-serial-number')}</SortableTh>
                <th className="text-left py-2 px-3 ct-eyebrow">{t('certificates.col-laboratories')}</th>
                <th className="text-center py-2 px-3 ct-eyebrow">{t('certificates.col-probes-count')}</th>
                <SortableTh column="orderNumber" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('certificates.col-order')}</SortableTh>
                <SortableTh column="customerName" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('certificates.col-customer')}</SortableTh>
                <th className="text-left py-2 px-3 ct-eyebrow">{t('certificates.col-approved-by')}</th>
                <th className="text-center py-2 px-3 ct-eyebrow">{t('certificates.col-downloads')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(cert => (
                <tr key={cert.id} className="ct-table-row">
                  <td className="px-2 py-2 w-8">
                    <Checkbox checked={sel.isSelected(cert.id)} onCheckedChange={() => sel.toggle(cert.id)} aria-label={`Vybrat ${cert.certificateNumber}`} />
                  </td>
                  <td className="py-2 px-3 font-mono" style={{ color: 'var(--ct-accent)' }}>{cert.certificateNumber}</td>
                  <td className="py-2 px-3 font-mono text-xs">{cert.approvedAt ? new Date(cert.approvedAt).toLocaleDateString(t('common.date-locale')) : '—'}</td>
                  <td className="py-2 px-3">{cert.instrumentName}</td>
                  <td className="py-2 px-3 font-mono text-xs">{cert.instrumentType}</td>
                  <td className="py-2 px-3 font-mono text-xs">{cert.serialNumber || '—'}</td>
                  <td className="py-2 px-3 text-xs capitalize">{(cert.laboratories || []).join(', ') || '—'}</td>
                  <td className="py-2 px-3 text-center font-mono text-xs">{cert.calibrationCount}</td>
                  <td className="py-2 px-3 font-mono text-xs">
                    {cert.orderNumber ? <RefLink type="order" id={cert.orderNumber} /> : '—'}
                  </td>
                  <td className="py-2 px-3 text-xs">{cert.customerName || '—'}</td>
                  <td className="py-2 px-3 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{cert.approvedByName || '—'}</td>
                  <td className="py-2 px-3 text-center">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`download-pdf-${cert.id}`}
                        onClick={() => downloadCertificatePdf(cert.id, cert.certificateNumber)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors hover:brightness-110"
                        style={{ color: 'var(--ct-accent)', border: '1px solid var(--ct-accent)' }}
                        title={t('certificates.btn-download-pdf-title')}
                      >
                        <FileDown className="w-3.5 h-3.5" /> PDF
                      </button>
                      {cert.hasDccXml && (
                        <button
                          data-testid={`download-dcc-${cert.id}`}
                          onClick={() => downloadDccXml(cert.id, cert.certificateNumber)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors"
                          style={{ color: 'var(--ct-success)', border: '1px solid var(--ct-success)' }}
                          title={t('certificates.btn-download-dcc-title')}
                        >
                          <FileCode className="w-3.5 h-3.5" /> XML
                        </button>
                      )}
                      <button
                        data-testid={`download-label-${cert.id}`}
                        onClick={() => downloadLabel(cert.id, cert.certificateNumber)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors"
                        style={{
                          color: cert.labelPrinted ? 'var(--ct-text-secondary)' : 'var(--ct-warning)',
                          border: `1px solid ${cert.labelPrinted ? 'var(--ct-text-secondary)' : 'var(--ct-warning)'}`
                        }}
                        title={cert.labelPrinted ? t('certificates.btn-print-label-printed-title', { date: new Date(cert.labelPrintedAt).toLocaleDateString(t('common.date-locale')) }) : t('certificates.btn-print-label-title')}
                      >
                        <Printer className="w-3.5 h-3.5" /> {t('certificates.btn-print-label')}
                      </button>
                      <button
                        data-testid={`delete-cert-${cert.id}`}
                        onClick={() => removeCert(cert.id, cert.certificateNumber)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors"
                        style={{ color: 'var(--ct-error, #dc2626)', border: '1px solid var(--ct-error, #dc2626)' }}
                        title="Smazat kalibrační list"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={12} className="text-center py-8" style={{ color: 'var(--ct-text-disabled)' }}>{t('certificates.empty')}</td></tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
      </div>
    </div>
  );
}
